import { Box, Checkbox, Flex, Image, Text } from '@chakra-ui/react'

import { useDeleteGraphic } from '@/api/graphics'
import IconTrash from '@/components/icons/IconTrash'
import Button from '@/components/ui/Button'
import { Tooltip } from '@/components/ui/Tooltip'

import type { Graphic } from '@/api/graphics'

interface GraphicCardProps {
  graphic: Graphic
  isSelected: boolean
  onSelect: (id: string) => void
  showButtons?: boolean
  onUseGraphic?: (id: string) => void
}

const CHECKBOX_SIZE = '16px'

function GraphicCard({
  graphic,
  isSelected,
  onSelect,
  showButtons = true,
  onUseGraphic
}: GraphicCardProps) {
  const deleteGraphic = useDeleteGraphic()

  const handleUseGraphic = (e: React.MouseEvent) => {
    e.stopPropagation()
    onUseGraphic?.(graphic.id)
  }

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation()
    deleteGraphic.mutate(graphic.id)
  }

  const handleSelect = () => {
    onSelect(graphic.id)
  }

  return (
    <Box
      w="100%"
      css={
        showButtons
          ? {
              '&:hover .graphic-card-image': {
                borderColor: 'var(--chakra-colors-accent-500)'
              },
              '&:hover .graphic-card-buttons': {
                opacity: 1,
                visibility: 'visible'
              },
              '&:hover .graphic-card-name': { opacity: 0, visibility: 'hidden' }
            }
          : {}
      }
    >
      <Box
        className="graphic-card-image"
        position="relative"
        w="100%"
        aspectRatio="1 / 1"
        bg="bg.tertiary"
        borderRadius="12px"
        border="1px solid"
        borderColor={isSelected ? 'accent.500' : 'transparent'}
        display="flex"
        alignItems="center"
        justifyContent="center"
        p="20px 24px 24px"
        transition="border-color 0.2s ease"
      >
        <Image
          src={graphic.url}
          alt={graphic.name}
          maxW="100%"
          maxH="100%"
          objectFit="contain"
        />

        <Box position="absolute" top="14px" right="14px">
          <Checkbox.Root
            checked={isSelected}
            onCheckedChange={handleSelect}
            size="sm"
            aria-label="Select graphic"
          >
            <Checkbox.HiddenInput />
            <Checkbox.Control
              w={CHECKBOX_SIZE}
              h={CHECKBOX_SIZE}
              bg="transparent"
              borderColor="text.tertiary"
              borderRadius="2px"
              borderWidth="1px"
              _checked={{
                bg: 'accent.500',
                borderColor: 'accent.500'
              }}
              cursor="pointer"
            >
              <Checkbox.Indicator />
            </Checkbox.Control>
          </Checkbox.Root>
        </Box>
      </Box>

      <Box position="relative" pt="6px" minH="36px">
        <Flex
          className="graphic-card-buttons"
          position="absolute"
          top="6px"
          left="0"
          right="0"
          opacity={0}
          visibility="hidden"
          gap="6px"
          align="center"
          transition="opacity 0.15s ease, visibility 0.15s ease"
        >
          <Tooltip content="Coming soon" positioning={{ placement: 'top' }}>
            <Button
              variant="primary"
              h="30px"
              flex="1"
              onClick={handleUseGraphic}
              disabled
            >
              Use Graphic
            </Button>
          </Tooltip>

          <Button
            variant="secondary"
            h="30px"
            w="30px"
            px={0}
            onClick={handleDelete}
            disabled={deleteGraphic.isPending}
            bg="bg.surface"
            borderColor="border.DEFAULT"
            _hover={{ bg: 'bg.tertiary' }}
          >
            <IconTrash boxSize="16px" color="text.secondary" />
          </Button>
        </Flex>

        <Box
          className="graphic-card-name"
          transition="opacity 0.15s ease, visibility 0.15s ease"
        >
          <Text fontWeight={600} color="text.primary" textStyle="bodySmall">
            {graphic.name}
          </Text>
        </Box>
      </Box>
    </Box>
  )
}

export default GraphicCard
